import React, { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import { Brand } from "../../context/BrandContext";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

type MinimalProduct = {
  id: number;
  name: string;
  image_url: string;
  brandId: number;
};

type BrandPreviewProps = {
  brand: Brand | null;
};

const BrandPreview = ({ brand }: BrandPreviewProps) => {
  const [products, setProducts] = useState<MinimalProduct[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!brand) return;

    const fetchProducts = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("Product")
        .select("id, name, image_url, brandId")
        .eq("brandId", brand.id);

      if (error) {
        console.error("❌ Failed to fetch brand products:", error.message);
        setProducts([]);
      } else {
        setProducts(data || []);
      }

      setLoading(false);
    };

    fetchProducts();
  }, [brand]);

  if (!brand) return null;

  return (
    <div className="my-12 text-center">
      <h2 className="text-2xl font-bold text-[#5c2f2f] mb-6">
        Products by {brand.name}
      </h2>

      <AnimatePresence mode="wait">
        {loading ? (
          <motion.p
            key="loading"
            className="text-[#7a4343]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            Loading products...
          </motion.p>
        ) : products.length === 0 ? (
          <motion.p
            key="empty"
            className="text-[#7a4343]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            No products found for this brand.
          </motion.p>
        ) : (
          <motion.div
            key={brand.id} // 🔑 triggers animation when brand changes
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.6 }}
            className="flex flex-wrap justify-center gap-8"
          >
            {products.map((product) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
              >
                <Link
                  to={`/product/${product.id}`}
                  className="w-[24rem] h-[30rem] bg-[#fff0f0] rounded-2xl p-4 flex flex-col items-center shadow-[0_6px_20px_rgba(92,47,47,0.2)] hover:shadow-[0_10px_28px_rgba(92,47,47,0.35)] transition"
                >
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-full h-[22rem] object-contain bg-transparent mb-4"
                  />
                  <p
                    className="
                      font-bold text-2xl text-[#5c2f2f] text-center
                      leading-tight
                      max-h-[3.5rem]
                      overflow-hidden text-ellipsis
                    "
                  >
                    {product.name}
                  </p>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BrandPreview;
